import { useRef } from 'react';
import { X } from 'lucide-react';

interface ImageUploaderProps {
    images: string[]; // data URL 或 http(s) URL
    onChange: (images: string[]) => void;
    label?: string;
    max?: number;
}

function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

// 图片上传组件：将本地图片转为 base64 data URL 提交给模型
export function ImageUploader({
    images,
    onChange,
    label = '上传图片',
    max = 4,
}: ImageUploaderProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        const remaining = Math.max(0, max - images.length);
        const list = Array.from(files).slice(0, remaining);
        const dataUrls = await Promise.all(list.map(fileToDataUrl));
        onChange([...images, ...dataUrls]);
        if (inputRef.current) inputRef.current.value = '';
    };

    const removeAt = (idx: number) => {
        onChange(images.filter((_, i) => i !== idx));
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
                {images.map((src, idx) => (
                    <div
                        key={idx}
                        className="relative h-16 w-16 overflow-hidden rounded-md border border-zinc-200 bg-zinc-50"
                    >
                        <img src={src} alt="" className="h-full w-full object-cover" />
                        <button
                            type="button"
                            onClick={() => removeAt(idx)}
                            className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-white"
                        >
                            <X size={10} />
                        </button>
                    </div>
                ))}
                {images.length < max && (
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="flex h-16 w-16 flex-col items-center justify-center rounded-md border border-dashed border-zinc-300 text-zinc-400 transition-colors hover:border-zinc-400 hover:text-zinc-500"
                    >
                        <span className="text-lg leading-none">+</span>
                        <span className="mt-0.5 text-[10px]">{label}</span>
                    </button>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                />
            </div>
            <p className="text-xs text-zinc-400">
                最多 {max} 张图片（本地预览，以 base64 方式提交给模型）
            </p>
        </div>
    );
}
